import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@gonka-wallet/ui-tokens';
import {
  deriveKeyFromPin,
  decrypt,
  zeroize,
  wrapKeyWithPrf,
  generatePrfSalt,
} from '@gonka-wallet/crypto';
import {
  openWalletDb,
  saveWallet,
  type EncryptedWalletBlob,
} from '@gonka-wallet/storage';
import { PinPad } from '../components/PinPad.js';
import { useWalletStore } from '../stores/walletStore.js';
import { useToastStore } from '../stores/toastStore.js';
import { hexToBytes, bytesToHex } from '../lib/format.js';
import {
  enrollBiometric,
  isWebAuthnAvailable,
} from '../lib/webauthn.js';

type Stage = 'overview' | 'pin' | 'enrolling';

function BiometricNav({ onBack, t }: { onBack?: () => void; t: (key: string) => string }) {
  return (
    <div className="flex items-center justify-between py-2">
      {onBack ? (
        <button type="button" onClick={onBack} className="text-link text-[15px]">
          ‹ {t('common.back')}
        </button>
      ) : (
        <div style={{ width: 36 }} />
      )}
      <div className="text-[16px] font-semibold text-text-primary">{t('biometric.navTitle')}</div>
      <div style={{ width: 36 }} />
    </div>
  );
}

export function Biometric() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wallet = useWalletStore((s) =>
    s.wallets.find((w) => w.id === s.activeId),
  );
  const setWallets = useWalletStore((s) => s.setWallets);
  const wallets = useWalletStore((s) => s.wallets);
  const pushToast = useToastStore((s) => s.push);
  const [stage, setStage] = useState<Stage>('overview');
  const [error, setError] = useState<string | null>(null);

  const supported = isWebAuthnAvailable();
  const enabled = !!wallet?.biometricWrap;

  if (!wallet) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-text-secondary">No wallet</p>
      </div>
    );
  }

  async function disableBiometric() {
    if (!wallet) return;
    setError(null);
    try {
      const updated: EncryptedWalletBlob = { ...wallet };
      delete updated.biometricWrap;
      const db = await openWalletDb();
      await saveWallet(db, updated);
      setWallets(wallets.map((w) => (w.id === updated.id ? updated : w)));
      pushToast(t('biometric.disabled'), 'info');
      navigate('/settings');
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function enableBiometric(pin: string) {
    if (!wallet) return;
    setStage('enrolling');
    setError(null);
    let derivedKey: Uint8Array = new Uint8Array(0);
    let plaintext: Uint8Array = new Uint8Array(0);
    let prfSecret: Uint8Array = new Uint8Array(0);
    try {
      // 1. Derive the seed-encryption key from the PIN, exactly the
      //    same way Unlock does. If the PIN is wrong this throws.
      const salt = hexToBytes(wallet.salt);
      const nonce = hexToBytes(wallet.nonce);
      const ciphertext = hexToBytes(wallet.ciphertext);
      derivedKey = await deriveKeyFromPin(pin, salt);
      plaintext = decrypt(derivedKey, nonce, ciphertext);

      // 2. Enroll a passkey + retrieve a 32-byte PRF secret.
      const prfSalt = generatePrfSalt();
      const enrollment = await enrollBiometric(
        wallet.id,
        wallet.name,
        prfSalt,
      );
      prfSecret = enrollment.prfSecret;

      // 3. Wrap the derived key under the PRF secret and store
      //    alongside the existing PIN-encrypted blob.
      const wrappedKey = await wrapKeyWithPrf(derivedKey, prfSecret);
      const updated: EncryptedWalletBlob = {
        ...wallet,
        biometricWrap: {
          credentialId: enrollment.credentialId,
          prfSalt: bytesToHex(prfSalt),
          wrappedKey: bytesToHex(wrappedKey),
        },
      };
      const db = await openWalletDb();
      await saveWallet(db, updated);
      setWallets(wallets.map((w) => (w.id === updated.id ? updated : w)));
      pushToast(t('biometric.enabled'), 'info');
      navigate('/settings');
    } catch (e) {
      const message = (e as Error).message;
      if (message.toLowerCase().includes('decrypt')) {
        setError(t('errors.crypto.decryptFailed'));
      } else if (
        message.toLowerCase().includes('cancel') ||
        message.toLowerCase().includes('not allowed')
      ) {
        setError(t('biometric.cancelled'));
      } else if (message.toLowerCase().includes('prf')) {
        setError(t('biometric.unsupported'));
      } else {
        setError(message);
      }
      setStage('pin');
    } finally {
      zeroize(derivedKey, plaintext, prfSecret);
    }
  }

  if (!supported) {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <BiometricNav onBack={() => navigate('/settings')} t={t} />
        <div className="mt-4 flex flex-1 flex-col gap-3.5">
          <p className="text-[13px] text-text-secondary">{t('biometric.notSupported')}</p>
        </div>
      </div>
    );
  }

  if (stage === 'pin') {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <BiometricNav onBack={() => setStage('overview')} t={t} />
        <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-6">
          <div className="text-center">
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
              {t('biometric.verifyPinLabel')}
            </div>
            <div className="mt-2 text-[18px] font-semibold tracking-[-0.4px] text-text-primary">
              {t('biometric.enterPin')}
            </div>
          </div>
          <PinPad onComplete={(pin) => void enableBiometric(pin)} />
          {error && (
            <div className="mt-2 text-center text-[11px] text-neg">{error}</div>
          )}
        </div>
      </div>
    );
  }

  if (stage === 'enrolling') {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <BiometricNav t={t} />
        <div className="mt-4 flex flex-1 flex-col gap-3.5">
          <div className="mx-auto mt-auto mb-auto flex flex-col items-center gap-4">
            <div className="flex h-[72px] w-[72px] items-center justify-center rounded-[20px] bg-surface-2 text-[36px] text-text-primary">
              ◉
            </div>
            <div className="text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
                {t('biometric.enrollingLabel')}
              </div>
              <div className="mt-1.5 text-[18px] font-semibold tracking-[-0.4px] text-text-primary">
                {t('biometric.prompt')}
              </div>
              <div className="mt-2 text-[13px] text-text-secondary">
                {t('biometric.enrollingHint')}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
      <BiometricNav onBack={() => navigate('/settings')} t={t} />
      <div className="mt-4 flex flex-1 flex-col gap-3.5">
        <div className="mx-auto mt-5 flex h-[72px] w-[72px] items-center justify-center rounded-[20px] bg-surface-2 text-[36px] text-text-primary">
          ◉
        </div>
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
            {t('biometric.overviewLabel')}
          </div>
          <div className="mt-1.5 text-[20px] font-semibold tracking-[-0.5px] text-text-primary">
            {enabled ? t('biometric.title') : t('biometric.title')}
          </div>
          <div className="mt-2 text-[13px] leading-relaxed text-text-secondary">
            {enabled ? t('biometric.enabledDescription') : t('biometric.description')}
          </div>
        </div>
        <div className="mt-auto flex flex-col gap-2.5">
          {enabled ? (
            <Button variant="danger" onClick={() => void disableBiometric()} fullWidth>
              {t('biometric.disable')}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => setStage('pin')} fullWidth>
              {t('biometric.enable')}
            </Button>
          )}
          {error && (
            <div className="text-center text-[11px] text-neg">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
}
